// Génération de PDF "document" (récapitulatif d'une fiche détail).
// Contrairement à l'export tabulaire (export.ts), ce module produit une mise en
// page soignée : en-tête de marque (nom + logo), titre, sections de champs,
// blocs de texte et tableaux, avec un pied de page (coordonnées + pagination).
// jsPDF est chargé à la demande pour ne jamais ralentir le chargement des pages.

import type { jsPDF } from "jspdf";
import { safeFilename } from "@/utils/export";
import { drawPdfFooter, drawPdfHeader, resolvePdfBrand, type PdfBrand } from "./pdfBrand";
import { loadPdfLibs } from "./pdfLibs";

export interface PdfField {
  label: string;
  value: string;
}

export interface PdfTable {
  columns: string[];
  rows: string[][];
}

export interface PdfSection {
  heading: string;
  /** Liste de champs "libellé / valeur". */
  fields?: PdfField[];
  /** Bloc de texte libre (description, conditions, notes...). */
  note?: string;
  /** Tableau de données. Ignoré s'il ne contient aucune ligne. */
  table?: PdfTable;
}

export interface PdfDocumentOptions {
  /** Nom du fichier sans extension. */
  filename: string;
  /** Titre principal du document. */
  title: string;
  /** Sous-titre optionnel (localisation, référence...). */
  subtitle?: string;
  sections: PdfSection[];
  /** Marque (nom + logo + coordonnées). Par défaut : marque de l'espace actif. */
  brand?: PdfBrand;
}

const MARGIN = 48;
const INK: [number, number, number] = [28, 28, 28];
const MUTED: [number, number, number] = [120, 120, 120];
const LINE: [number, number, number] = [225, 225, 225];
const SOFT: [number, number, number] = [246, 246, 246];
const DARK: [number, number, number] = [38, 38, 38];

function filenameStamp(): string {
  const now = new Date();
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}-${pad(now.getHours())}${pad(now.getMinutes())}`;
}

function lastTableY(doc: unknown, fallback: number): number {
  const finalY = (doc as { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY;
  return typeof finalY === "number" ? finalY : fallback;
}

/** Construit le document PDF (en-tête de marque, sections, pied de page). */
async function buildSummaryPdf(options: PdfDocumentOptions): Promise<jsPDF> {
  const { JsPDF, autoTable } = await loadPdfLibs();

  const doc = new JsPDF({ orientation: "portrait", unit: "pt", format: "a4" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const bottomLimit = pageHeight - MARGIN - 36;
  const brand = resolvePdfBrand(options.brand);

  let y = drawPdfHeader(doc, brand, MARGIN);

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...INK);
  const titleLines = doc.splitTextToSize(options.title, contentWidth);
  doc.text(titleLines, MARGIN, y);
  y += titleLines.length * 22;

  if (options.subtitle) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10.5);
    doc.setTextColor(...MUTED);
    const subLines = doc.splitTextToSize(options.subtitle, contentWidth);
    doc.text(subLines, MARGIN, y + 2);
    y += subLines.length * 15;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(150, 150, 150);
  y += 6;
  doc.text(`Document généré le ${new Date().toLocaleString("fr-FR")}`, MARGIN, y);
  y += 16;

  doc.setDrawColor(...LINE);
  doc.setLineWidth(0.8);
  doc.line(MARGIN, y, pageWidth - MARGIN, y);
  y += 26;

  const ensureSpace = (needed: number) => {
    if (y + needed > bottomLimit) {
      doc.addPage();
      y = MARGIN;
    }
  };

  options.sections.forEach((section, index) => {
    const isEmpty =
      (!section.fields || section.fields.length === 0) &&
      !section.note &&
      (!section.table || section.table.rows.length === 0);
    if (isEmpty) return;

    if (index > 0) ensureSpace(54);

    // Titre de section.
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(...INK);
    doc.text(section.heading.toUpperCase(), MARGIN, y);
    doc.setDrawColor(...LINE);
    doc.setLineWidth(0.6);
    doc.line(MARGIN, y + 6, pageWidth - MARGIN, y + 6);
    y += 18;

    if (section.fields && section.fields.length > 0) {
      autoTable(doc, {
        startY: y,
        body: section.fields.map((field) => [field.label, field.value]),
        theme: "plain",
        styles: {
          fontSize: 9,
          cellPadding: { top: 3, bottom: 3, left: 0, right: 8 },
          overflow: "linebreak",
          valign: "top",
        },
        columnStyles: {
          0: { cellWidth: contentWidth * 0.38, textColor: MUTED },
          1: { cellWidth: contentWidth * 0.62, textColor: INK, fontStyle: "bold" },
        },
        margin: { left: MARGIN, right: MARGIN, bottom: MARGIN + 30 },
      });
      y = lastTableY(doc, y) + 16;
    }

    if (section.note) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      doc.setTextColor(60, 60, 60);
      const lines = doc.splitTextToSize(section.note, contentWidth);
      ensureSpace(lines.length * 12 + 10);
      doc.text(lines, MARGIN, y + 6);
      y += lines.length * 12 + 14;
    }

    if (section.table && section.table.rows.length > 0) {
      autoTable(doc, {
        startY: y,
        head: [section.table.columns],
        body: section.table.rows,
        theme: "striped",
        styles: { fontSize: 8.5, cellPadding: 5, overflow: "linebreak" },
        headStyles: { fillColor: DARK, textColor: 255, fontStyle: "bold" },
        alternateRowStyles: { fillColor: SOFT },
        margin: { left: MARGIN, right: MARGIN, bottom: MARGIN + 30 },
      });
      y = lastTableY(doc, y) + 16;
    }
  });

  drawPdfFooter(doc, brand, MARGIN);

  return doc;
}

/**
 * Génère et télécharge un PDF récapitulatif à partir des sections fournies.
 * Tous les contenus passés proviennent des données réellement affichées.
 */
export async function exportSummaryPdf(options: PdfDocumentOptions): Promise<void> {
  const doc = await buildSummaryPdf(options);
  doc.save(`${safeFilename(options.filename)}-${filenameStamp()}.pdf`);
}

/**
 * Génère le récapitulatif puis lance l'impression papier (même mise en page que
 * le PDF, en-tête de marque et pied de page inclus).
 */
export async function printSummaryPdf(options: PdfDocumentOptions): Promise<void> {
  const doc = await buildSummaryPdf(options);
  doc.autoPrint();

  const blobUrl = doc.output("bloburl").toString();
  const printWindow = window.open(blobUrl, "_blank");
  if (printWindow) return;

  // Repli si l'ouverture d'un nouvel onglet est bloquée : impression via iframe.
  const blob = doc.output("blob");
  const url = URL.createObjectURL(blob);
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.style.opacity = "0";
  iframe.src = url;
  document.body.appendChild(iframe);
  window.setTimeout(() => {
    document.body.removeChild(iframe);
    URL.revokeObjectURL(url);
  }, 60000);
}